import React from 'react';

import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import AvomaHoverCard from 'components/Common/AvomaHoverCard';
import OverflowingText from 'components/Common/OverflowingText';
import { isDesktop } from 'helpers/deviceCutoffs';
import Tokens from 'styles/tokens';

const IndividualDetailValue = ({ detail, showSeparator, separator, items }) => {
  if (!isEmpty(items)) {
    const itemByLookup = items?.find(p => p.label === detail);

    return (
      <React.Fragment>
        <div
          className='flex items-center overflow-hidden rounded p-2'
          style={{
            backgroundColor:
              itemByLookup?.backgroundColor || Tokens.colors.whiteSmoke,
            color: itemByLookup?.foregroundColor || Tokens.colors.gunmetal
          }}
        >
          <OverflowingText
            className='text-x2s font-bold uppercase'
            tooltip={itemByLookup?.label}
          >
            {itemByLookup?.label}
          </OverflowingText>
        </div>
        {showSeparator && (
          <span className='shrink-0 text-xs uppercase text-silver'>
            {separator}
          </span>
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className='flex items-center truncate rounded bg-whiteSmoke py-1 px-2'>
        <OverflowingText className='text-sm font-bold'>
          {detail}
        </OverflowingText>
      </div>
      {showSeparator && (
        <span className='shrink-0 text-xs uppercase text-silver'>
          {separator}
        </span>
      )}
    </React.Fragment>
  );
};

IndividualDetailValue.propTypes = {
  detail: PropTypes.string,
  showSeparator: PropTypes.bool,
  separator: PropTypes.string,
  items: PropTypes.array
};

const FilterSelectionViewer = ({
  label,
  details = [],
  render,
  separator = 'or',
  placeholder = 'Select',
  isRange = false,
  items = [] // Used to lookup the label in cases where details refer to non-label data (e.g. Purposes details refers to UUIDs but we want the Purpose names)
}) => {
  if (render) {
    return render({ label, details });
  }

  const isDesktopScreen = isDesktop();

  const detailLimit = isDesktopScreen ? 2 : 3;

  if (details.length === 0)
    return <div className='w-full px-2 text-sm text-silver'>{placeholder}</div>;

  if (isRange) {
    return (
      <div className='flex w-full items-center gap-2 px-2'>
        <div className='flex items-center rounded bg-whiteSmoke px-2 py-1 text-sm font-bold'>
          {details[0]} - {details[1]}
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2 px-2'>
      {details
        ?.filter(d => d)
        .slice(0, detailLimit)
        .map((detail, i) => (
          <IndividualDetailValue
            key={i}
            detail={detail}
            showSeparator={i !== details.slice(0, detailLimit).length - 1}
            separator={separator}
            items={items}
          />
        ))}
      {details.length > detailLimit && (
        <AvomaHoverCard
          triggerComponent={
            <div className='flex shrink-0 items-center gap-2'>
              <span className='text-xs uppercase text-silver'>{separator}</span>
              <div className='flex items-center rounded bg-whiteSmoke px-2 py-1 text-sm font-bold'>
                +{details.length - detailLimit}
              </div>{' '}
            </div>
          }
          contentClasses='z-[999999] !max-w-[250px] !w-fit max-h-[300px] overflow-y-auto'
        >
          <div className='flex flex-col gap-2'>
            {details?.slice(detailLimit).map((detail, i) => {
              if (!isEmpty(items)) {
                const itemByLookup = items?.find(p => p.label === detail);

                return (
                  <React.Fragment key={detail}>
                    <div
                      className='truncate rounded px-2 pb-1'
                      style={{
                        backgroundColor:
                          itemByLookup?.backgroundColor ||
                          Tokens.colors.whiteSmoke,
                        color:
                          itemByLookup?.foregroundColor ||
                          Tokens.colors.gunmetal
                      }}
                    >
                      <span className='text-x2s font-bold uppercase'>
                        {itemByLookup?.label}
                      </span>
                    </div>
                    {i !== details.slice(detailLimit).length - 1 && (
                      <span className='text-center text-xs uppercase text-silver'>
                        {separator}
                      </span>
                    )}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={i}>
                  <div className='flex items-center rounded bg-whiteSmoke px-2 py-1 text-sm font-bold'>
                    {detail}{' '}
                  </div>
                  {i !== details.slice(detailLimit).length - 1 && (
                    <span className='text-center text-xs uppercase text-silver'>
                      {separator}
                    </span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </AvomaHoverCard>
      )}
    </div>
  );
};
FilterSelectionViewer.propTypes = {
  details: PropTypes.string,
  label: PropTypes.string,
  render: PropTypes.func,
  separator: PropTypes.string,
  placeholder: PropTypes.string,
  isRange: PropTypes.bool,
  items: PropTypes.array
};

export default FilterSelectionViewer;
